// @ts-nocheck
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config/config";

export const AuthContext = createContext<any>(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const [accessToken, setAccessToken] = useState(
    () => localStorage.getItem("accessToken") || "",
  );

  const [refreshToken, setRefreshToken] = useState(
    () => localStorage.getItem("refreshToken") || "",
  );

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const failedRequestsQueue = useRef([]);

  const isTokenExpired = useCallback((token) => {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime + 30;
    } catch {
      return true;
    }
  }, []);

  const setTokens = useCallback((newAccessToken, newRefreshToken) => {
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);

    localStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("refreshToken", newRefreshToken);
  }, []);

  const clearTokens = useCallback(() => {
    setAccessToken("");
    setRefreshToken("");
    setUser(null);

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
  }, []);

  const processFailedQueue = useCallback((error, token = null) => {
    failedRequestsQueue.current.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    failedRequestsQueue.current = [];
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await axios.post(
        `${config.API_BASE_URL}/api/auth/refresh`,
        {
          refreshToken,
        },
      );

      const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
        response.data;
      setTokens(newAccessToken, newRefreshToken);

      return newAccessToken;
    } catch (error) {
      console.error("❌ Token refresh failed:", error);
      clearTokens();
      navigate("/login", { replace: true });
      throw error;
    }
  }, [refreshToken, setTokens, clearTokens, navigate]);

  const getValidAccessToken = useCallback(async () => {
    if (accessToken && !isTokenExpired(accessToken)) {
      return accessToken;
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedRequestsQueue.current.push({ resolve, reject });
      });
    }

    setIsRefreshing(true);

    try {
      const newToken = await refreshAccessToken();
      processFailedQueue(null, newToken);
      return newToken;
    } catch (error) {
      processFailedQueue(error, null);
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [
    accessToken,
    isTokenExpired,
    isRefreshing,
    refreshAccessToken,
    processFailedQueue,
  ]);

  const login = async (username, password) => {
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/auth/login`, {
        username,
        password,
      });

      console.log("✅ Login Response:", res.data);

      const {
        user: userData,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
      } = res.data;

      setUser(userData);
      setTokens(newAccessToken, newRefreshToken);

      localStorage.setItem("user", JSON.stringify(userData));

      navigate("/home", { replace: true });
    } catch (err) {
      console.error("❌ Login failed:", err.response?.data || err.message);

      throw new Error(
        err.response?.data?.message || "Invalid username or password.",
      );
    }
  };

  const register = async (username, email, password, role = 'user') => {
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/auth/register`, {
        username,
        email,
        password,
        role,
      });

      console.log("✅ Registration Response:", res.data);
      return res.data;
    } catch (err) {
      console.error("❌ Registration failed:", err.response?.data || err.message);

      throw new Error(
        err.response?.data?.error || "Registration failed.",
      );
    }
  };

  const logout = useCallback(async () => {
    try {
      if (refreshToken) {
        await axios.post(`${config.API_BASE_URL}/api/auth/logout`, {
          refreshToken,
        });
      }
    } catch (error) {
      console.error("❌ Logout request failed:", error);
    } finally {
      clearTokens();

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 0);
    }
  }, [refreshToken, clearTokens, navigate]);

  const authenticatedAxios = useCallback(
    async (config) => {
      try {
        const token = await getValidAccessToken();

        const requestConfig = {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await axios(requestConfig);
        return response;
      } catch (error) {
        if (
          error.response?.status === 401 &&
          error.response?.data?.code === "TOKEN_EXPIRED"
        ) {
          try {
            setAccessToken("");
            const newToken = await getValidAccessToken();

            const retryConfig = {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${newToken}`,
              },
            };

            return await axios(retryConfig);
          } catch (retryError) {
            console.error("❌ Retry after token refresh failed:", retryError);
            logout();
            throw retryError;
          }
        }

        throw error;
      }
    },
    [getValidAccessToken, logout],
  );

  const isAuthenticated = useCallback(() => {
    return !!refreshToken && !!user;
  }, [refreshToken, user]);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        const storedAccessToken = localStorage.getItem("accessToken");
        const storedRefreshToken = localStorage.getItem("refreshToken");
        const storedUser = localStorage.getItem("user");

        if (storedAccessToken && storedRefreshToken && storedUser) {
          setAccessToken(storedAccessToken);
          setRefreshToken(storedRefreshToken);
          setUser(JSON.parse(storedUser));

          if (isTokenExpired(storedAccessToken)) {
            try {
              await refreshAccessToken();
            } catch (error) {
              console.error("❌ Failed to refresh token on init:", error);
              clearTokens();
            }
          }
        }
      } catch (error) {
        console.error("❌ Auth initialization error:", error);
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isTokenExpired, refreshAccessToken, clearTokens]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      async (config) => {
        if (
          config.url?.includes("/auth/login") ||
          config.url?.includes("/auth/register") ||
          config.url?.includes("/auth/refresh")
        ) {
          return config;
        }

        try {
          const token = await getValidAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.error("❌ Failed to get token for request:", error);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          error.response?.data?.code === "TOKEN_EXPIRED" &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          try {
            const newToken = await getValidAccessToken();
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error(
              "❌ Token refresh failed in interceptor:",
              refreshError,
            );
            logout();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [getValidAccessToken, logout]);

  const contextValue = {
    user,
    accessToken,
    refreshToken,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    authenticatedAxios,
    getValidAccessToken,
    token: accessToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
